'use client';

import {
  ChevronRight,
  CreditCard,
  ExternalLink,
  FlaskConical,
  Loader2,
  Maximize2,
  Plug,
  Truck,
} from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import Confetti from 'react-confetti';
import { useTranslation } from 'react-i18next';

import {
  buildSetupProductsListInput,
  SETUP_CATALOG_LIST_PAGE_SIZE,
} from '@/components/setup/experience/lib/setupCatalogSummary';
import { pickFirstCheckoutLineItem } from '@/components/setup/experience/lib/setupCheckoutLineItem';
import {
  buildSetupCheckoutPreviewUrl,
  extractCheckoutUrlFromFunnel,
  SETUP_CHECKOUT_IFRAME_SANDBOX,
} from '@/components/setup/experience/lib/setupCheckoutUtils';
import { useSetupActivePayment } from '@/components/setup/experience/hooks/useSetupActivePayment';
import { useSetupShippingSummary } from '@/components/setup/experience/hooks/useSetupShippingSummary';
import {
  buildSetupIntegrationSummaryKey,
  buildSetupShippingBadgeLabel,
} from '@/components/setup/experience/lib/setupCompleteDisplay';
import type { SetupSourcePath } from '@/components/setup/experience/types';
import { ORG_ROUTES, STORE_ROUTES } from '@/config/router';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useOrgNavigate } from '@/hooks/use-app-navigate';
import { useFunnel } from '@/lib/api/funnels';
import { useProductsList } from '@/lib/api/products';
import { completeOnboarding } from '@/lib/onboarding';

interface SetupCompleteStepProps {
  orgSlug: string;
  storeId: string;
  storeName: string;
  baseCurrency: string;
  funnelId: string;
  sourcePath: SetupSourcePath | null;
}

export function SetupCompleteStep({
  orgSlug,
  storeId,
  storeName,
  baseCurrency,
  funnelId,
  sourcePath,
}: SetupCompleteStepProps) {
  const { t, i18n } = useTranslation(['setup', 'shippings']);
  const xp = 'experience.complete';
  const { navigate } = useOrgNavigate();
  const prefersReducedMotion = useReducedMotion();
  const storeRoutes = useMemo(() => STORE_ROUTES(orgSlug, storeId), [orgSlug, storeId]);
  const orgRoutes = useMemo(() => ORG_ROUTES(orgSlug), [orgSlug]);

  const [windowSize, setWindowSize] = useState({ width: 0, height: 0 });
  const [showConfetti, setShowConfetti] = useState(!prefersReducedMotion);
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);

  const { rates: shippingRates, summary: shippingSummary } = useSetupShippingSummary(
    storeId,
    baseCurrency,
    t('experience.shipping.freeSummary'),
    t('shippings:table.allCountries'),
    Boolean(storeId),
  );

  const { active: activePayment } = useSetupActivePayment(storeId);
  const paymentSummary = activePayment?.summary ?? null;

  const { data: productsData, isLoading: isLoadingProducts } = useProductsList(
    buildSetupProductsListInput(storeId, SETUP_CATALOG_LIST_PAGE_SIZE),
    { enabled: !!storeId },
  );

  const lineItem = useMemo(
    () => pickFirstCheckoutLineItem(productsData?.items, baseCurrency),
    [productsData?.items, baseCurrency],
  );

  // The preview's `/checkout/init` redirect needs the funnel's mounted checkout
  // URL as an override — without it the default host resolution is broken for
  // studio funnels and the redirect 404s ("Page Not Found").
  const funnelQuery = useFunnel(funnelId, storeId);
  const checkoutUrl = extractCheckoutUrlFromFunnel(funnelQuery.data);

  const editorHref = storeRoutes.STOREFRONT_EDITOR(funnelId);
  const integrationKey = buildSetupIntegrationSummaryKey(sourcePath);

  const testCheckoutUrl = useMemo(() => {
    // Hold off until the funnel's checkout URL resolves, otherwise the redirect
    // falls back to the broken default host resolution and 404s.
    if (!checkoutUrl) return null;
    return buildSetupCheckoutPreviewUrl({
      storeId,
      funnelId,
      currency: baseCurrency,
      checkoutUrl,
      lineItem,
      i18nLanguage: i18n.language,
      shippingSnapshot: shippingSummary,
      shippingRates,
    });
  }, [baseCurrency, checkoutUrl, funnelId, i18n.language, lineItem, shippingRates, shippingSummary, storeId]);

  const nextSteps = useMemo(
    () => [
      {
        id: 'production',
        title: t(`${xp}.nextSteps.production.title`),
        hint: t(`${xp}.nextSteps.production.hint`),
        href: orgRoutes.PROCESSORS(),
      },
      {
        id: 'domain',
        title: t(`${xp}.nextSteps.domain.title`),
        hint: t(`${xp}.nextSteps.domain.hint`),
        href: storeRoutes.STORE_EDIT(),
      },
      {
        id: 'products',
        title: t(`${xp}.nextSteps.products.title`),
        hint: t(`${xp}.nextSteps.products.hint`),
        href: storeRoutes.PRODUCTS(),
      },
      {
        id: 'checkout',
        title: t(`${xp}.nextSteps.checkout.title`),
        hint: t(`${xp}.nextSteps.checkout.hint`),
        href: editorHref,
      },
    ],
    [editorHref, orgRoutes, storeRoutes, t, xp],
  );

  useEffect(() => {
    const updateSize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    };

    updateSize();
    window.addEventListener('resize', updateSize);

    if (prefersReducedMotion) return () => window.removeEventListener('resize', updateSize);

    const timer = window.setTimeout(() => setShowConfetti(false), 5500);

    return () => {
      window.removeEventListener('resize', updateSize);
      window.clearTimeout(timer);
    };
  }, [prefersReducedMotion]);

  useEffect(() => {
    if (!isCheckoutModalOpen || !testCheckoutUrl) return;
    setIsCheckoutLoading(true);
    // A cross-origin or blocked storefront may never fire the iframe's onLoad
    // (frame-ancestors deny, a failed redirect, or no storefront host in local
    // dev), which would leave the spinner running forever. Clear it after a
    // timeout so the dialog can't get stuck on a blank frame — the "open in new
    // tab" fallback stays reachable underneath.
    const spinnerFallback = window.setTimeout(() => setIsCheckoutLoading(false), 10000);
    return () => window.clearTimeout(spinnerFallback);
  }, [isCheckoutModalOpen, testCheckoutUrl]);

  const finishSetup = () => {
    navigate(completeOnboarding({ orgSlug, storeId }));
  };

  const openCheckoutPreview = () => {
    if (!testCheckoutUrl) return;
    setIsCheckoutLoading(true);
    setIsCheckoutModalOpen(true);
  };

  const openLiveStore = () => {
    if (!testCheckoutUrl) return;
    window.open(testCheckoutUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      {showConfetti && windowSize.width > 0 ? (
        <div className="pointer-events-none fixed inset-0 z-50" aria-hidden>
          <Confetti
            width={windowSize.width}
            height={windowSize.height}
            recycle={false}
            numberOfPieces={280}
            gravity={0.22}
            colors={['#22c55e', '#86efac', '#fbbf24', '#f472b6', '#60a5fa', '#c084fc']}
          />
        </div>
      ) : null}

      <motion.div
        className="relative mx-auto w-full max-w-[90rem] px-0 pb-10 pt-0"
        initial={prefersReducedMotion ? false : { opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="grid grid-cols-[minmax(0,1fr)] items-start gap-5 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:items-stretch lg:gap-6">
          <div className="flex min-w-0 flex-auto flex-col gap-5">
            <section className="bg-card flex flex-col gap-5 rounded-xl border border-[color-mix(in_oklch,var(--border)_88%,transparent)] p-6 shadow-[0_1px_3px_color-mix(in_oklch,var(--foreground)_5%,transparent)] md:p-7">
              <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
                <div className="min-w-0 flex-[1_1_16rem]">
                  <p className="m-0 mb-1.5 text-[0.6875rem] font-semibold uppercase tracking-[0.06em] text-[color-mix(in_oklch,var(--primary)_72%,var(--muted-foreground))]">
                    {t(`${xp}.kicker`)}
                  </p>
                  <h1 className="text-foreground m-0 text-xl font-semibold leading-tight tracking-[-0.025em] sm:text-2xl">
                    {t(`${xp}.headline`, { storeName })}
                  </h1>
                  <p className="text-muted-foreground mt-1.5 max-w-[34rem] text-sm leading-[1.55]">
                    {t(`${xp}.lead`)}
                  </p>
                </div>
                <p className="m-0 shrink-0 rounded-full bg-[color-mix(in_oklch,#10b981_12%,var(--card))] px-3 py-1.5 text-xs font-semibold text-[#059669]">
                  {t(`${xp}.salesStatusReady`)}
                </p>
              </div>

              {testCheckoutUrl ? (
                <Button
                  type="button"
                  variant="link"
                  className="text-foreground inline-flex h-auto w-fit cursor-pointer items-center gap-1.5 border-0 bg-transparent p-0 px-0 font-[inherit] text-[0.8125rem] font-semibold underline underline-offset-[3px] transition-colors duration-150 hover:text-[color-mix(in_oklch,var(--primary)_80%,var(--foreground))]"
                  onClick={openLiveStore}
                >
                  <span>{t(`${xp}.openStore`)}</span>
                  <ExternalLink className="size-3.5" strokeWidth={2} aria-hidden />
                </Button>
              ) : null}

              <div className="flex flex-wrap gap-2">
                <div className="text-foreground inline-flex items-center gap-1.5 rounded-full border border-[color-mix(in_oklch,var(--border)_80%,transparent)] bg-[color-mix(in_oklch,var(--muted)_18%,var(--card))] px-3 py-1.5 text-xs font-semibold">
                  <FlaskConical className="size-3.5" strokeWidth={2} aria-hidden />
                  <span>{t(`${xp}.statusTestMode`)}</span>
                </div>
                <div className="text-foreground inline-flex items-center gap-1.5 rounded-full border border-[color-mix(in_oklch,var(--border)_80%,transparent)] bg-[color-mix(in_oklch,var(--muted)_18%,var(--card))] px-3 py-1.5 text-xs font-semibold">
                  <CreditCard className="size-3.5" strokeWidth={2} aria-hidden />
                  <span>
                    {paymentSummary?.processorName
                      ? t(`${xp}.badgePayment`, { processor: paymentSummary.processorName })
                      : t(`${xp}.summary.payment.fallback`)}
                  </span>
                </div>
              </div>

              <div className="flex flex-wrap gap-2.5">
                <Button type="button" onClick={finishSetup}>
                  {t(`${xp}.finish`)}
                </Button>
                <Button type="button" variant="secondary" onClick={() => navigate(editorHref)}>
                  {t(`${xp}.openStudio`)}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={openCheckoutPreview}
                  disabled={!testCheckoutUrl}
                >
                  {t(`${xp}.viewCheckout`)}
                </Button>
              </div>

              <div className="grid grid-cols-1 gap-4 border-t border-[color-mix(in_oklch,var(--border)_75%,transparent)] pt-5 md:grid-cols-3">
                <div className="flex items-start gap-3">
                  <span className="text-primary flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[color-mix(in_oklch,var(--primary)_10%,var(--muted))]">
                    <CreditCard className="size-4" strokeWidth={2} aria-hidden />
                  </span>
                  <div>
                    <h3 className="text-foreground m-0 mb-1 text-sm font-semibold">
                      {t(`${xp}.summary.payment.title`)}
                    </h3>
                    <p className="text-muted-foreground m-0 text-[0.8125rem] leading-[1.45]">
                      {paymentSummary?.processorName
                        ? t(`${xp}.summary.payment.configured`, { processor: paymentSummary.processorName })
                        : t(`${xp}.summary.payment.fallback`)}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <span className="text-primary flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[color-mix(in_oklch,var(--primary)_10%,var(--muted))]">
                    <Truck className="size-4" strokeWidth={2} aria-hidden />
                  </span>
                  <div>
                    <h3 className="text-foreground m-0 mb-1 text-sm font-semibold">
                      {t(`${xp}.summary.shipping.title`)}
                    </h3>
                    <p className="text-muted-foreground m-0 text-[0.8125rem] leading-[1.45]">
                      {shippingSummary
                        ? t(`${xp}.summary.shipping.configured`, {
                            name: buildSetupShippingBadgeLabel(
                              shippingSummary,
                              t(`${xp}.summary.shipping.fallback`),
                            ),
                            zone: shippingSummary.previewRateCountries,
                          })
                        : t(`${xp}.summary.shipping.fallback`)}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <span className="text-primary flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[color-mix(in_oklch,var(--primary)_10%,var(--muted))]">
                    <Plug className="size-4" strokeWidth={2} aria-hidden />
                  </span>
                  <div>
                    <h3 className="text-foreground m-0 mb-1 text-sm font-semibold">
                      {t(`${xp}.summary.integration.title`)}
                    </h3>
                    <p className="text-muted-foreground m-0 text-[0.8125rem] leading-[1.45]">
                      {t(`${xp}.summary.integration.${integrationKey}`)}
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <section className="bg-card rounded-xl border border-[color-mix(in_oklch,var(--border)_88%,transparent)] p-5 shadow-[0_1px_3px_color-mix(in_oklch,var(--foreground)_5%,transparent)]">
              <h2 className="text-foreground m-0 mb-3 text-[0.8125rem] font-bold">
                {t(`${xp}.nextSteps.title`)}
              </h2>
              <ul className="m-0 flex list-none flex-col gap-2 p-0">
                {nextSteps.map((step) => (
                  <li key={step.id}>
                    <button
                      type="button"
                      className="bg-card flex w-full cursor-pointer items-center justify-between gap-3 rounded-[0.625rem] border border-[color-mix(in_oklch,var(--border)_82%,transparent)] px-3.5 py-3 text-left transition-[background-color] duration-150 hover:bg-[color-mix(in_oklch,var(--muted)_24%,var(--card))]"
                      onClick={() => navigate(step.href)}
                    >
                      <span className="flex min-w-0 flex-col gap-0.5">
                        <span className="text-foreground text-[0.8125rem] font-semibold">{step.title}</span>
                        <span className="text-muted-foreground text-xs">{step.hint}</span>
                      </span>
                      <ChevronRight className="text-muted-foreground size-4 shrink-0" aria-hidden />
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          </div>

          <aside className="lg:order-0 order-[-1] flex min-w-0 flex-col lg:sticky lg:top-4 lg:w-auto lg:flex-none">
            <section className="bg-card flex min-h-[min(72dvh,36rem)] flex-col rounded-xl border border-[color-mix(in_oklch,var(--border)_88%,transparent)] p-5 shadow-[0_1px_3px_color-mix(in_oklch,var(--foreground)_5%,transparent)] lg:min-h-full lg:p-6">
              <div className="mb-4 flex items-center justify-between gap-3">
                <h2 className="text-foreground m-0 text-[0.9375rem] font-bold">
                  {t(`${xp}.checkoutPreview.title`)}
                </h2>
                <button
                  type="button"
                  className="bg-card text-foreground inline-flex min-h-8 shrink-0 cursor-pointer items-center gap-1.5 rounded-full border border-[color-mix(in_oklch,var(--border)_88%,transparent)] px-3.5 py-1.5 font-[inherit] text-xs font-semibold transition-[background-color] duration-150 hover:enabled:bg-[color-mix(in_oklch,var(--muted)_30%,transparent)] disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={openCheckoutPreview}
                  disabled={!testCheckoutUrl}
                >
                  <Maximize2 className="size-3.5" strokeWidth={2} aria-hidden />
                  {t(`${xp}.checkoutPreview.action`)}
                </button>
              </div>

              <button
                type="button"
                className="block min-h-[18rem] w-full flex-1 cursor-pointer overflow-hidden rounded-[0.625rem] border border-[color-mix(in_oklch,var(--border)_80%,transparent)] bg-[color-mix(in_oklch,var(--muted)_16%,transparent)] p-0 text-left hover:enabled:border-[color-mix(in_oklch,var(--primary)_35%,var(--border))] disabled:cursor-wait"
                onClick={openCheckoutPreview}
                disabled={!testCheckoutUrl}
                aria-label={t(`${xp}.viewCheckout`)}
              >
                {testCheckoutUrl ? (
                  <iframe
                    title={t('experience.checkoutPreview.iframeTitle')}
                    src={testCheckoutUrl}
                    className="bg-card pointer-events-none block h-[min(52dvh,22rem)] w-full border-0 lg:h-full lg:min-h-[min(64dvh,32rem)]"
                    sandbox={SETUP_CHECKOUT_IFRAME_SANDBOX}
                    tabIndex={-1}
                  />
                ) : isLoadingProducts || funnelQuery.isLoading ? (
                  <div className="flex h-[min(52dvh,22rem)] items-center justify-center lg:h-full lg:min-h-[min(64dvh,32rem)]">
                    <Loader2 className="text-muted-foreground size-5 animate-spin" aria-hidden />
                  </div>
                ) : (
                  <div className="flex h-[min(52dvh,22rem)] items-center justify-center lg:h-full lg:min-h-[min(64dvh,32rem)]">
                    <p>{t(`${xp}.noProducts`)}</p>
                    <button
                      type="button"
                      className="bg-card text-foreground inline-flex min-h-8 shrink-0 cursor-pointer items-center gap-1.5 rounded-full border border-[color-mix(in_oklch,var(--border)_88%,transparent)] px-3.5 py-1.5 font-[inherit] text-xs font-semibold transition-[background-color] duration-150 hover:enabled:bg-[color-mix(in_oklch,var(--muted)_30%,transparent)] disabled:cursor-not-allowed disabled:opacity-50"
                      onClick={() => navigate(storeRoutes.PRODUCTS())}
                    >
                      {t(`${xp}.addProducts`)}
                    </button>
                  </div>
                )}
              </button>
            </section>
          </aside>
        </div>
      </motion.div>

      <Dialog open={isCheckoutModalOpen} onOpenChange={setIsCheckoutModalOpen}>
        <DialogContent className="flex max-h-[min(92dvh,880px)] w-[min(96vw,920px)] max-w-none flex-col gap-0 overflow-hidden p-0 sm:max-w-none">
          <DialogHeader className="space-y-1 border-b px-5 py-4 text-left sm:px-6">
            <DialogTitle className="text-base font-semibold leading-snug tracking-tight">
              {t(`${xp}.checkoutModalTitle`)}
            </DialogTitle>
          </DialogHeader>

          <div className="relative min-h-[min(72dvh,720px)] flex-1 overflow-hidden bg-[color-mix(in_oklch,var(--muted)_10%,var(--background))]">
            {isCheckoutLoading && testCheckoutUrl ? (
              <div
                className="text-muted-foreground absolute inset-0 z-[2] flex flex-col items-center justify-center gap-2.5 bg-[color-mix(in_oklch,var(--background)_78%,transparent)] text-[0.8125rem] font-medium backdrop-blur-[2px]"
                role="status"
                aria-live="polite"
              >
                <Loader2 className="text-primary size-5 animate-spin" aria-hidden />
                <span>{t('experience.checkout.previewLoading')}</span>
              </div>
            ) : null}

            {testCheckoutUrl ? (
              <iframe
                key={testCheckoutUrl}
                title={t('experience.checkoutPreview.iframeTitle')}
                src={testCheckoutUrl}
                className="bg-card block h-[min(72dvh,720px)] w-full border-0"
                sandbox={SETUP_CHECKOUT_IFRAME_SANDBOX}
                onLoad={() => setIsCheckoutLoading(false)}
                onError={() => setIsCheckoutLoading(false)}
              />
            ) : null}
          </div>

          {testCheckoutUrl ? (
            <div className="flex justify-end border-t px-5 py-3 sm:px-6">
              <a
                href={testCheckoutUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-foreground inline-flex items-center gap-1.5 text-[0.6875rem] font-semibold no-underline"
              >
                <ExternalLink className="size-3.5" strokeWidth={2} aria-hidden />
                {t('experience.checkoutPreview.openTab')}
              </a>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
